import { OptionalGroupRepository } from "core/repository";
import { Base2Presenter } from "core/presenter";
import { OptionalGroupsConstraint } from "core/constraint";
import { OptionalGroup } from "core/entity";
import validate from "util/validate";
import _ from "lodash";
import Big from "big.js";

class OptionalGroupFormCreatePresenter extends Base2Presenter {
  initialState() {
    return {
      form: new OptionalGroup(),
      loading: true,
      errors: {
        name: "",
        limited: false,
        limitedValue: "",
        mandatory: false,
        mandatory_quantity: "",
        value: "",
        optionalNameErrors: [],
        optionalMandatoryErrors: []
      },
      title: "Novo Grupo de Opcionais"
    };
  }

  async onStart() {
    this.enqueue({ loading: true });
    await this.loadOptionalGroup();
    this.enqueue({ loading: false });
  }

  async loadOptionalGroup() {
    const optionalGroupResult = await this.getOptionalGroup();

    if (optionalGroupResult.success) {
      this.enqueue({ form: optionalGroupResult.data });
    } else {
      this.enqueue({
        error:
          "Não conseguimos carregar esse grupo de opcionais! Tente novamente em alguns minutos."
      });

      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content: this.viewModel.error
      });
    }
  }

  async getOptionalGroup() {
    return { success: true, data: new OptionalGroup() };
  }

  async onSendForm() {
    this.enqueue({ loading: true });

    const params = _.clone(this.viewModel.form);

    const errors = this.validateForm(params);

    // Assure the error object has only one error and all optionalErrors are empty
    const emptyOptionalErrors =
      Object.keys(errors).length <= 2 &&
      errors.optionalNameErrors.every(opt => opt === "") &&
      errors.optionalMandatoryErrors.every(opt => opt === "");

    if (Object.keys(errors).length === 0 || emptyOptionalErrors) {
      const parsedParams = this.parseFormValues(params);
      const response = await this.callRepository(parsedParams);

      if (response.success) {
        this.sendNotification({
          type: "success",
          content: "Grupo de Opcionais salvo!"
        });
        return this.navigateTo("app.registration.optional_group");
      } else {
        this.sendNotification({
          type: "error",
          title: "Ooops",
          content:
            "Ocorreu um problema ao salvar o grupo de opcionais. Verifique os dados informados e se os campos marcados com * estão preenchidos."
        });
      }
    } else {
      this.enqueue({ errors });

      this.sendNotification({
        type: "error",
        title: "Ooops",
        content:
          "Ocorreu um problema ao salvar o grupo de opcionais. Verifique os dados informados e se os campos marcados com * estão preenchidos."
      });
    }

    this.enqueue({ loading: false });
  }

  validateForm(params) {
    let errors = {};
    const validation = validate(params, OptionalGroupsConstraint.general, {
      fullMessages: false
    });

    if (!!validation) {
      Object.entries(validation)
        .map(([field, errors]) => ({ field, content: errors.join(", ") }))
        .forEach(({ field, content }) => (errors[field] = content));
    }

    const optionalNameErrors = this.validateOptionalsName(params.optionals);
    errors.optionalNameErrors = optionalNameErrors;

    // const optionalMandatoryErrors = this.validateOptionalMandatory(params.optionals, params.mandatory_quantity, params.limited_value);
    const optionalMandatoryErrors = Array(params.optionals.slice().length).fill(
      ""
    );
    errors.optionalMandatoryErrors = optionalMandatoryErrors;

    return errors;
  }

  validateOptionalsName(optionals) {
    const optionalNameErrors = [];
    optionals.forEach(optional => {
      if (optional.name === "") {
        optionalNameErrors.push("Opção sem nome");
      } else {
        optionalNameErrors.push("");
      }
      return;
    });
    return optionalNameErrors;
  }

  validateOptionalMandatory(optionals, mandatory_quantity, limited_value) {
    const optionalMandatoryErrors = [];
    const minimum = parseInt(mandatory_quantity, 10) || 0;
    const maximum = parseInt(limited_value, 10) || 0;
    const amountChecked = optionals.filter(o => o.mandatory === true).length;
    let message = "";
    let boolToBeChecked = mandatory_quantity && !limited_value ? false : true;
    let options = "opções";
    let mandatory = "Obrigatório";
    let checkAll = false;

    if (limited_value && maximum < minimum) {
      message = `Verifique os valores mínimos e máximos de opcionais informados`;
      checkAll = true;
    } else if (
      minimum &&
      maximum &&
      (amountChecked < minimum || amountChecked > maximum)
    ) {
      message = `Marque entre ${minimum} e ${maximum} ${options} como ${mandatory}`;
      if (minimum === maximum) {
        if (minimum === 1) {
          options = "opção";
          mandatory = "obrigatória";
        }
        message = `Marque exatamente ${minimum} ${options} como ${mandatory}`;
      }
      boolToBeChecked = amountChecked > maximum;
    } else {
      if (minimum && amountChecked < minimum) {
        if (minimum === 1) {
          options = "opção";
          mandatory = "obrigatória";
        }
        message = `Marque no mínimo ${minimum} ${options} como ${mandatory}`;
      } else if (maximum && amountChecked > maximum) {
        if (maximum === 1) {
          options = "opção";
          mandatory = "obrigatória";
          boolToBeChecked = true;
        }
        message = `Marque no máximo ${maximum} ${options} como ${mandatory}`;
      }
    }

    if (checkAll) {
      optionals.forEach(_ => {
        optionalMandatoryErrors.push(message);
        return;
      });
    } else {
      optionals.forEach(optional => {
        if (optional.mandatory === boolToBeChecked) {
          optionalMandatoryErrors.push(message);
        } else {
          optionalMandatoryErrors.push("");
        }
        return;
      });
    }

    return optionalMandatoryErrors;
  }

  parseFormValues(params) {
    let parsedValues = params;
    if (!params.limited) {
      parsedValues.limited_value = null;
    }

    if (params.limited && params.limited_value) {
      parsedValues.limited_value = parseInt(params.limited_value, 10);
    }

    if (!params.mandatory) {
      parsedValues.mandatory_quantity = null;
    }

    if (params.mandatory && params.mandatory_quantity) {
      parsedValues.mandatory_quantity = parseInt(params.mandatory_quantity, 10);
    }

    const newOptionals = parsedValues.optionals.filter(optional => {
      return optional.name !== "";
    });

    parsedValues.optionals = newOptionals.map(opt => {
      let value = opt.value ? parseInt(Big(opt.value).times(100), 10) : null;
      return { mandatory: opt.mandatory, name: opt.name, value };
    });
    parsedValues.optionals = _.uniqWith(parsedValues.optionals, _.isEqual);
    if (params.value) {
      parsedValues.value = parseInt(params.value, 10);
    } else {
      parsedValues.value = 0;
    }

    return parsedValues;
  }

  async callRepository(params) {
    return await OptionalGroupRepository.createOptionalGroup(params);
  }

  handleNewOptional() {
    const optionals = this.viewModel.form.optionals;
    optionals.push({ mandatory: false, name: "", value: null });
    this.enqueue({ optionals });
  }

  enqueueFormOptionals(optionals) {
    this.enqueue(vm => ({
      form: {
        ...vm.form,
        optionals
      }
    }));
  }

  onChangeOptional(value, index, field) {
    let newOptionals = this.viewModel.form.optionals;
    newOptionals[index][field] = value;
    this.enqueueFormOptionals(newOptionals);
  }

  onDeleteOptional(index) {
    const optionals = this.viewModel.form.optionals;
    optionals.splice(index, 1);
    let newOptionals = [...optionals];

    if (optionals.length === 0) {
      newOptionals = [{ name: "" }];
    }

    this.enqueueFormOptionals(newOptionals);
  }

  onChange(field, value) {
    this.enqueue(vm => ({
      form: {
        ...vm.form,
        [field]: value
      }
    }));
  }

  onChangeMandatoryCheckBox(value) {
    this.enqueue(
      vm => ({
        form: {
          ...vm.form,
          mandatory: value
        }
      }),
      () => {
        if (value === false) {
          this.onChange("mandatory_quantity", "");
        }
      }
    );
  }

  onChangeLimitedCheckBox(value) {
    this.enqueue(
      vm => ({
        form: {
          ...vm.form,
          limited: value
        }
      }),
      () => {
        if (value === false) {
          this.onChange("limited_value", "");
        }
      }
    );
  }

  onGoBack() {
    this.navigateTo("app.registration.optional_group");
  }
}

export default OptionalGroupFormCreatePresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/optional-groups/form/create.presenter.js